import { useCallback, useMemo, useState } from 'react';

/**
 * Client-side table state: free-text search, per-column filters, sort. The
 * standard ATEK pattern is fetch-all (or fetch a large page, ~1000–5000 rows)
 * and refine in memory — see the frontend-react-layout skill rule "Default to
 * fetch-all + client-side filter; Pagination is an escape hatch."
 *
 * Pair with `DataTable` from `@/shared/components/data-table`:
 *
 * ```tsx
 * const t = useClientSideTable({
 *   items: invoices,
 *   searchFields: (inv) => [inv.number, inv.customer_name],
 *   initialSortKey: 'date',
 *   initialSortDirection: -1,
 * });
 * <DataTable
 *   items={t.sortedItems}
 *   columns={columns}
 *   ctx={ctx}
 *   columnFilters={t.columnFilters}
 *   sortKey={t.sortKey}
 *   sortDirection={t.sortDirection}
 *   onSort={t.setSort}
 *   onSetColumnFilter={t.setColumnFilter}
 *   emptyState={<EmptyState isFiltered={t.isFiltered} onClearFilters={t.clear} />}
 * />
 * ```
 */
export interface UseClientSideTableOptions<T> {
  items: T[];
  /** Free-text search target. Return any string fields you want to match. */
  searchFields?: (item: T) => Array<string | number | null | undefined>;
  /**
   * Per-column accessor. Maps a column key to the value used for filtering and
   * sorting. Default: `(item, key) => item[key]`. Override for nested fields.
   */
  getColumnValue?: (item: T, key: string) => unknown;
  initialSortKey?: string;
  initialSortDirection?: 1 | -1;
  initialSearch?: string;
  initialColumnFilters?: Record<string, string>;
}

export interface UseClientSideTableResult<T> {
  /** Filtered + sorted items, ready to render. */
  sortedItems: T[];
  /** Item count after filtering. */
  total: number;

  sortKey: string;
  sortDirection: 1 | -1;
  setSort: (key: string) => void;

  columnFilters: Record<string, string>;
  setColumnFilter: (key: string, value: string) => void;

  search: string;
  setSearch: (value: string) => void;

  /** True if any filter or search is active. */
  isFiltered: boolean;
  /** Clear search + every column filter. */
  clear: () => void;
}

function defaultAccessor(item: unknown, key: string): unknown {
  if (item && typeof item === 'object') {
    return (item as Record<string, unknown>)[key];
  }
  return undefined;
}

function matchesFilter(value: unknown, filter: string): boolean {
  if (!filter) return true;
  if (value === null || value === undefined) return false;
  return String(value).toLowerCase().includes(filter.toLowerCase());
}

function compare(a: unknown, b: unknown): number {
  if (a === b) return 0;
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

export function useClientSideTable<T>({
  items,
  searchFields,
  getColumnValue = defaultAccessor,
  initialSortKey = '',
  initialSortDirection = 1,
  initialSearch = '',
  initialColumnFilters = {},
}: UseClientSideTableOptions<T>): UseClientSideTableResult<T> {
  const [sortKey, setSortKey] = useState(initialSortKey);
  const [sortDirection, setSortDirection] = useState<1 | -1>(initialSortDirection);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(initialColumnFilters);
  const [search, setSearch] = useState(initialSearch);

  const setSort = useCallback((key: string) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDirection((d) => (d === 1 ? -1 : 1));
        return prev;
      }
      setSortDirection(1);
      return key;
    });
  }, []);

  const setColumnFilter = useCallback((key: string, value: string) => {
    setColumnFilters((prev) => {
      if (!value) {
        const { [key]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: value };
    });
  }, []);

  const clear = useCallback(() => {
    setColumnFilters({});
    setSearch('');
  }, []);

  const sortedItems = useMemo(() => {
    const lowerSearch = search.trim().toLowerCase();
    const activeFilters = Object.entries(columnFilters);

    const filtered = items.filter((item) => {
      if (lowerSearch && searchFields) {
        const haystack = searchFields(item)
          .filter((v) => v !== null && v !== undefined)
          .map((v) => String(v).toLowerCase());
        if (!haystack.some((s) => s.includes(lowerSearch))) return false;
      }
      for (const [key, filterValue] of activeFilters) {
        if (!matchesFilter(getColumnValue(item, key), filterValue)) return false;
      }
      return true;
    });

    if (!sortKey) return filtered;

    return [...filtered].sort((a, b) => {
      const av = getColumnValue(a, sortKey);
      const bv = getColumnValue(b, sortKey);
      return compare(av, bv) * sortDirection;
    });
  }, [items, search, columnFilters, sortKey, sortDirection, searchFields, getColumnValue]);

  const isFiltered = search.length > 0 || Object.keys(columnFilters).length > 0;

  return {
    sortedItems,
    total: sortedItems.length,
    sortKey,
    sortDirection,
    setSort,
    columnFilters,
    setColumnFilter,
    search,
    setSearch,
    isFiltered,
    clear,
  };
}
